import { DropdownOption, SignUpError, SignUpProps, utils } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';
import React, { Component, SyntheticEvent } from 'react';
import { Query } from 'react-apollo';
import styled from 'styled-components';

import {
  Lab,
  LabsQuery,
  InputContainer,
  VMarginWrapper,
  ButtonWrapper,
  TwoFieldWrapper,
  InputWrapperMargin,
  DisclaimerText,
  GenderInputWrapper,
  QUERY_LABS,
  sortLabs
} from '../../../components/SignUpFormShared';
import Button from '../../../components/Button/NewButton';
import SelectInput, { SelectWithOptions } from '../../../components/SelectInput';
import TextInput from '../../../components/TextInput/NewTextInput';
import CreatePasswordInput from './CreatePasswordInput';
import TermsOfUse from 'components/TermsOfUse';

const ButtonWrapperPrivacy = styled(ButtonWrapper)`
  padding-bottom: 8px;
`;

const {
  GENDER_INPUT_DEFAULTS,
  PRONOUN_INPUT_DEFAULTS,
  COUNTRIES_OPTIONS,
  US_COUNTRY_OPTION,
  isEmailValid,
  isPasswordValid,
  isInputValid,
  isPhoneNumberValid,
  isDOBValid,
  isDateValid,
  isLabValid,
  states
} = utils;

interface OptionParams {
  label: string;
  value: string;
}

type Props = {
  mobile: boolean;
  preselectedLab: Lab | null;
  userConfirmationState: string | null;
  includeMembershipTerms: boolean;
  loading: boolean;
  signUpError: SignUpError | null;
  signUpUser: (props: SignUpProps) => void;
  onSuccess: (state: string) => void;
  onError: (arg0: string) => void;
  onUserAlreadyExists: () => void;
};

interface Error {
  email: boolean;
  password: boolean;
  firstName: boolean;
  lastName: boolean;
  gender: boolean;
  birthdate: boolean;
  dob: boolean;
  phone: boolean;
  patientLab: boolean;
  membershipTerms: boolean;
  country: boolean;
  zipCode: boolean;
  city: boolean;
}

type State = {
  emailAddress: string;
  firstName: string;
  lastName: string;
  gender: string;
  genderOption: OptionParams | null;
  pronoun: string;
  pronounOption: OptionParams | null;
  birthdate: string;
  phone: string;
  membershipTerms: boolean;
  password: string;
  errors: Error;
  patientLab: Lab | null;
  referralSource: string;
  addressLine: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  countryOption: OptionParams | null;
};

export default class SignUpWithEmail extends Component<Props, State> {
  state: State = {
    emailAddress: '',
    firstName: '',
    lastName: '',
    gender: GENDER_INPUT_DEFAULTS[1].value, // female
    genderOption: GENDER_INPUT_DEFAULTS[1], // { label: 'female', value: 'female' }
    pronoun: '',
    pronounOption: { label: '', value: '' },
    birthdate: '',
    phone: '',
    membershipTerms: false,
    password: '',
    errors: { password: false },
    patientLab: null,
    addressLine: '',
    addressLine2: '',
    city: '',
    state: '',
    stateOption: { label: '', value: '' },
    zipCode: '',
    country: US_COUNTRY_OPTION.value,
    countryOption: US_COUNTRY_OPTION
  };

  sortedLabs = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      ...this.state,
      patientLab: props.preselectedLab
    };
  }

  componentDidMount() {
    if (window.analytics) {
      window.analytics.page('Email Sign Up');
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    if (newProps.userConfirmationState === 'unconfirmed') {
      this.props.onSuccess(newProps.userConfirmationState);
    }
    if (newProps.signUpError) {
      const onError = this.props.onError;
      switch (newProps.signUpError.type) {
        case 'Unknown':
        default:
          onError('Sorry, we were unable to create your account. Please try again.');
          break;
        case 'UserNameExists':
          onError(
            'We are unable to proceed with your request. In case you have an account already, please log in using the same method as your registration or create a new account using a different email.'
          );
          setTimeout(() => {
            this.props.onUserAlreadyExists();
          }, 5000);
          break;
      }
    }
  }

  isEmptyString = (str: string) => str.match(/^ *$/) !== null;

  isUS = (country: string) => country === 'United States' || country === 'USA';

  updatePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      password: event.currentTarget.value
    });
  };

  updatePatientLab = (value: Lab) => {
    this.setState({
      patientLab: value
    });
  };

  updateReferralSource = ({ value }: DropdownOption) => {
    this.setState({ referralSource: value });
  };

  onUpdateInputText = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      [event.target.name]: event.target.value
    });
  };

  onUpdateSelect = (option: OptionParams, action: unknown) => {
    this.setState({
      [action.name]: option.value,
      [`${[action.name]}Option`]: option
    });
  };

  onUpdateCountry = (option: OptionParams, action: unknown) => {
    this.onUpdateSelect(option, action);

    if (this.isUS(option.value)) {
      return this.updatePatientLab('');
    }

    const labOther = this.sortedLabs.find(item => item.name === 'Other');
    this.updatePatientLab(labOther);
  };

  async _handleSignup(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    const { onError } = this.props;

    const fieldErrors = this._getFieldErrors();
    if (fieldErrors) {
      const { errorStrings, errors } = fieldErrors;

      const somethingWentWrongString = 'Something went wrong!';
      let errorMessage = somethingWentWrongString;
      if (errorStrings.length > 0) {
        if (errors.referralSource) {
          errorMessage = 'Please let us know how you heard about us';
        }
        if (errors.email) {
          errorMessage = 'Please enter a valid email.';
        }
        if (errors.password) {
          errorMessage =
            'Please enter a valid password. At least 8 characters with uppercase, lowercase, and special characters.';
        }
        if (errors.dob) {
          errorMessage = 'Patients must be 18 years or older to schedule a consult.';
        }
        if (errors.birthdate) {
          errorMessage = 'Please enter a valid date.';
        }
        if (errors.membershipTerms) {
          errorMessage = 'You must agree to terms.';
        }
        if (errors.phone) {
          errorMessage = 'You must provide a phone number.';
        }
        if (errors.gender) {
          errorMessage = 'You must select a gender.';
        }
        if (errors.patientLab) {
          errorMessage = 'You must select a location.';
        }
        if (errors.firstName) {
          errorMessage = 'Please enter a valid first name.';
        }
        if (errors.lastName) {
          errorMessage = 'Please enter a valid last name.';
        }
        if (errors.addressLine) {
          errorMessage = 'Please enter an address line';
        }
        if (errors.city) {
          errorMessage = 'Please enter a city name';
        }
        if (errors.state) {
          errorMessage = 'Please select a state';
        }
        if (errors.zipCode) {
          errorMessage = 'Please enter a Zip Code';
        }
        if (errors.country) {
          errorMessage = 'Please select a country';
        }
      } else {
        errorMessage = errorStrings.reduce((agr, error, index, array) => {
          const message = `${agr} ${error}`;
          if (index + 1 !== array.length && array.length > 1) {
            return `${message}, `;
          }
          return message;
        }, 'Please fix the following fields:');
      }
      this.setState(() => ({ errors }));
      onError(errorMessage);

      if (errorMessage === somethingWentWrongString) {
        Honeybadger.notify('Something went wrong shown to the user', 'SignUpError');
      }

      return;
    }
    // TODO: Better handle this - should never get here due to above validation.
    // This silences the potential flow error below though that patientLab may be null
    if (!this.state.patientLab) {
      return;
    }

    const signUpProps: SignUpProps = {
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      gender: this.state.gender,
      pronoun: this.state.pronoun,
      birthdate: this.state.birthdate,
      phone: this.state.phone,
      email: this.state.emailAddress,
      password: this.state.password,
      labId: parseInt(this.state.patientLab.id),
      referralSource: this.state.referralSource,
      address: {
        address1: this.state.addressLine,
        address2: this.state.addressLine2,
        city: this.state.city,
        state: this.state.state,
        zipcode: this.state.zipCode,
        country: this.state.country
      }
    };

    if (window.analytics) {
      window.analytics.track('Email Sign Up Started');
    }

    await this.props.signUpUser(signUpProps);
  }

  _getFieldErrors = () => {
    const {
      firstName,
      lastName,
      gender,
      birthdate,
      phone,
      emailAddress,
      password,
      membershipTerms,
      patientLab,
      addressLine,
      city,
      state,
      zipCode,
      country,
      referralSource
    } = this.state;

    const errorStrings = [];
    const errors: { [key: string]: boolean } = {};

    if (!isInputValid(firstName)) {
      errorStrings.push('First name');
      errors.firstName = true;
    }
    if (!isInputValid(lastName)) {
      errorStrings.push('Last name');
      errors.lastName = true;
    }
    if (!isInputValid(gender)) {
      errors.gender = true;
    }
    if (!isDOBValid(birthdate)) {
      errorStrings.push('Birthdate');
      errors.dob = true;
    }
    if (!isDateValid(birthdate)) {
      errorStrings.push('Birthdate');
      errors.birthdate = true;
    }
    if (this.isUS(country) && !isPhoneNumberValid(phone)) {
      errorStrings.push('Phone number');
      errors.phone = true;
    }
    if (!this.isUS(country) && phone.length <= 1) {
      errorStrings.push('Phone number');
      errors.phone = true;
    }
    // @ts-ignore
    if (!isLabValid(patientLab)) {
      errorStrings.push('Kindbody Home Location');
      errors.patientLab = true;
    }
    if (!isEmailValid(emailAddress)) {
      errorStrings.push('Email');
      errors.email = true;
    }
    if (!isPasswordValid(password)) {
      errorStrings.push('Password');
      errors.password = true;
    }
    if (this.isEmptyString(addressLine)) {
      errorStrings.push('Address Line');
      errors.addressLine = true;
    }
    if (this.isEmptyString(city)) {
      errorStrings.push('City');
      errors.city = true;
    }
    if (this.isUS(country) && this.isEmptyString(state)) {
      errorStrings.push('State');
      errors.state = true;
    }
    if (this.isUS(country) && this.isEmptyString(zipCode)) {
      errorStrings.push('Zip Code');
      errors.zipCode = true;
    }
    if (this.isEmptyString(country)) {
      errorStrings.push('Country');
      errors.country = true;
    }
    if (!referralSource || this.isEmptyString(referralSource)) {
      errorStrings.push('Please let us know how you heard about us.');
      errors.referralSource = true;
    }

    if (this.props.includeMembershipTerms && !membershipTerms) {
      errorStrings.push('You must agree to terms.');
      errors.membershipTerms = true;
    }
    if (errorStrings.length === 0 && Object.keys(errors).length === 0) return false;

    return {
      errors,
      errorStrings
    };
  };

  render() {
    const {
      birthdate,
      emailAddress,
      errors,
      firstName,
      genderOption,
      pronounOption,
      lastName,
      password,
      patientLab,
      phone,
      addressLine,
      addressLine2,
      city,
      zipCode,
      country,
      countryOption,
      state,
      referralSource,
      stateOption
    } = this.state;

    return (
      <form onSubmit={this._handleSignup.bind(this)} autoComplete="on">
        <InputContainer>
          <VMarginWrapper>
            <TextInput
              name="emailAddress"
              label="EMAIL ADDRESS"
              value={emailAddress}
              onChange={this.onUpdateInputText}
              error={errors.email}
              autoComplete="email"
              spellCheck="false"
              placeholder="janedoe@email.com"
              inputMode="email"
            />
          </VMarginWrapper>

          <VMarginWrapper>
            <TwoFieldWrapper>
              <InputWrapperMargin>
                <TextInput
                  name="firstName"
                  label="FIRST NAME"
                  value={firstName}
                  onChange={this.onUpdateInputText}
                  error={errors.firstName}
                  autoComplete="given-name"
                  spellCheck="false"
                  placeholder="First name"
                />
              </InputWrapperMargin>
              <InputWrapperMargin>
                <TextInput
                  name="lastName"
                  label="LAST NAME"
                  value={lastName}
                  onChange={this.onUpdateInputText}
                  error={errors.lastName}
                  autoComplete="family-name"
                  spellCheck="false"
                  placeholder="Last name"
                />
              </InputWrapperMargin>
            </TwoFieldWrapper>
            <DisclaimerText>
              Be sure to enter your full legal name for your medical record.
            </DisclaimerText>
          </VMarginWrapper>

          <VMarginWrapper>
            <SelectInput
              name="country"
              label="COUNTRY"
              extractTitle={item => (item ? item.label : '')}
              value={country}
              placeholder="Select country"
              selectedOption={countryOption}
              onSelect={this.onUpdateCountry}
              options={COUNTRIES_OPTIONS}
            />
          </VMarginWrapper>

          <TwoFieldWrapper>
            <InputWrapperMargin>
              <TextInput
                name="addressLine"
                label="ADDRESS LINE 1"
                value={addressLine}
                onChange={this.onUpdateInputText}
                placeholder="Address"
                // @ts-ignore
                error={errors.addressLine}
              />
            </InputWrapperMargin>
            <InputWrapperMargin>
              <TextInput
                name="addressLine2"
                label="ADDRESS LINE 2"
                value={addressLine2}
                onChange={this.onUpdateInputText}
                placeholder="Address"
                // @ts-ignore
                error={errors.addressLine}
              />
            </InputWrapperMargin>
          </TwoFieldWrapper>

          {this.isUS(country) ? (
            <VMarginWrapper>
              <SelectInput
                name="state"
                label="STATE"
                type="select"
                placeholder="Select state"
                selectedOption={stateOption}
                extractTitle={item => (item ? item.label : '')}
                options={states}
                onSelect={this.onUpdateSelect}
              />
            </VMarginWrapper>
          ) : (
            <InputWrapperMargin>
              <TextInput
                label="STATE"
                name="state"
                value={state}
                onChange={this.onUpdateInputText}
                placeholder="State"
              />
            </InputWrapperMargin>
          )}

          <TwoFieldWrapper>
            <InputWrapperMargin>
              <TextInput
                name="city"
                label="CITY"
                placeholder="City"
                value={city}
                onChange={this.onUpdateInputText}
                error={errors.city}
              />
            </InputWrapperMargin>

            {this.isUS(country) ? (
              <InputWrapperMargin>
                <TextInput
                  name="zipCode"
                  label="ZIP"
                  value={zipCode}
                  inputType="zip"
                  placeholder="55555"
                  autoComplete="postal-code"
                  onChange={this.onUpdateInputText}
                  error={errors.zipCode}
                />
              </InputWrapperMargin>
            ) : (
              <InputWrapperMargin>
                <TextInput
                  name="zipCode"
                  label="ZIP"
                  value={zipCode}
                  placeholder="ZIP"
                  onChange={this.onUpdateInputText}
                  error={errors.zipCode}
                />
              </InputWrapperMargin>
            )}
          </TwoFieldWrapper>

          <VMarginWrapper>
            <TwoFieldWrapper>
              <InputWrapperMargin>
                <TextInput
                  inputType={this.isUS(country) ? 'phone' : 'text'}
                  name="phone"
                  label="PHONE NUMBER"
                  placeholder="(+XXXX)Phone Number"
                  autoComplete="tel"
                  value={phone}
                  onChange={this.onUpdateInputText}
                  error={errors.phone}
                  inputMode="tel"
                />
              </InputWrapperMargin>
              <InputWrapperMargin>
                <TextInput
                  inputType="date"
                  name="birthdate"
                  label="BIRTHDATE"
                  autoComplete="bday"
                  value={birthdate}
                  onChange={this.onUpdateInputText}
                  error={errors.birthdate || errors.dob}
                  mobile={this.props.mobile}
                />
              </InputWrapperMargin>
            </TwoFieldWrapper>
          </VMarginWrapper>

          <VMarginWrapper>
            <TwoFieldWrapper>
              <GenderInputWrapper>
                <SelectInput
                  name="gender"
                  label="SEX AT BIRTH"
                  extractTitle={item => (item ? item.label : '')}
                  placeholder="Select"
                  selectedOption={genderOption}
                  onSelect={this.onUpdateSelect}
                  options={GENDER_INPUT_DEFAULTS}
                  error={errors.gender}
                />
              </GenderInputWrapper>
              <InputWrapperMargin>
                <SelectInput
                  name="pronoun"
                  label="PRONOUN"
                  extractTitle={item => (item ? item.label : '')}
                  placeholder="Pronoun"
                  selectedOption={pronounOption}
                  onSelect={this.onUpdateSelect}
                  options={PRONOUN_INPUT_DEFAULTS}
                />
              </InputWrapperMargin>
            </TwoFieldWrapper>
          </VMarginWrapper>

          <VMarginWrapper>
            <Query query={QUERY_LABS}>
              {({ data }: LabsQuery) => {
                this.sortedLabs = data?.labs.sort(sortLabs);

                return (
                  <SelectInput
                    label="KINDBODY HOME LOCATION"
                    extractTitle={lab => (lab ? lab.name : '')}
                    placeholder="Select city"
                    selectedOption={patientLab}
                    onSelect={this.updatePatientLab}
                    options={(data && this.sortedLabs) || []}
                    error={errors.patientLab}
                  />
                );
              }}
            </Query>
          </VMarginWrapper>

          <VMarginWrapper id="referralSource">
            <SelectWithOptions
              label="HOW DID YOU HEAR ABOUT US?"
              value={referralSource}
              placeholder={'Select one option'}
              lookup_key="patient_referral_source"
              onSelect={this.updateReferralSource}
            />
          </VMarginWrapper>

          <CreatePasswordInput
            value={password}
            onChange={this.updatePassword}
            error={errors.password}
          />
          <ButtonWrapperPrivacy>
            <Button text={'SIGN UP'} type="submit" loading={this.props.loading} />
          </ButtonWrapperPrivacy>
          <TermsOfUse />
        </InputContainer>
      </form>
    );
  }
}
